export type StripeAddress = {
  city?: string;
  country?: string;
  line1?: string;
  line2?: string;
  postal_code?: string;
  state?: string;
};

export type PurchaseResult =
  | { type: 'success'; purchaseId: string }
  | { type: 'action'; action: any }
  | {
      type: 'clientSecret';
      clientSecret: string;
      purchaseId: string;
      confirmType: 'payment' | 'setup';
    };

export type BasePurchaseOptions = {
  priceId: string;
  couponId?: string;
  stripePaymentMethod?: string | undefined;
  address?: StripeAddress;
  customAmountInCents?: number;
  /**
   * The buyer submitted a campaign checkout that showed the SimpleCheck
   * consent notice. Only honored when the campaign has it turned on.
   */
  simpleCheckConsent?: boolean;
  /** Optional phone from the SimpleCheck form; goes to OP "SMS Number". */
  phone?: string;
  /**
   * Temporary: the name of the offers the user accepted.
   */
  offers?: string[];
};

/**
 * Stored on `pmts.purchases.metadata.simpleCheckConsent` at submit. Written
 * to Ontraport once the purchase goes through; `at` is stamped then, so a
 * repeat notifyOntraport keeps the first time.
 */
export type SimpleCheckConsentRecord = {
  version: string;
  ip?: string;
  phone?: string;
  at?: string;
};

export type CheckoutFn = (opts: { priceId: string; couponId?: string }) => Promise<boolean>;

export type StripeHandlerFn = (opts: { checkout: CheckoutFn }) => Promise<boolean | undefined>;

export type PurchaseFn = (
  opts: Partial<BasePurchaseOptions>,
) => Promise<PurchaseResult | undefined>;

export type PriceSummary = {
  id: string;
  name: string;
  paymentType: string;
  priceInCents: number;
  currency: string;
  numPayments?: number;
  interval?: string;
  freeTrialPeriod?: number;
};

/**
 * Display payload for the AIP repeat-trialer "backup offer" on a campaign
 * checkout. When a returning member who already used an AIP free trial hits a
 * campaign that grants a fresh trial, the trial is replaced with a discounted
 * first month (server-enforced). This drives the on-page banner + CTA so the
 * display matches what the server will actually charge. See
 * `memory/aip-backup-offer.md`.
 */
export type AipBackupOfferDisplay = {
  /** True when the first-month coupon for the price's currency applies (US$50 / CA$68 off, price above the discount). */
  applyBackup: boolean;
  /** First charge, e.g. "$47" (discounted) or the full recurring price when applyBackup is false. */
  firstChargeLabel: string;
  /** Recurring price, e.g. "$97/month". */
  recurringLabel: string;
  /** Formatted first-charge date when the cohort defers the start (e.g. "July 6, 2026"); omitted when charged immediately. */
  chargeDateLabel?: string;
  /** CTA button text replacing the free-trial CTA. */
  ctaText: string;
  /** Banner heading, e.g. "Welcome back". */
  bannerHeading: string;
  /** Banner body explaining why this isn't another free trial. */
  bannerText: string;
};

/**
 * Whether a subscription is paused right now.
 *
 * `pmts.purchases.paused_until` records when a pause ends, and the value is
 * kept afterwards so we can still tell which renewal reminders a past pause
 * swallowed. Presence therefore does not mean paused — the date decides.
 */
export function isPauseActive(pausedUntil: Date | string | undefined | null) {
  return !!pausedUntil && new Date(pausedUntil).getTime() > Date.now();
}
